import React from 'react'
import { Alert, Divider, Flex, Skeleton, Space, Table, Typography } from 'antd'
import { useSelector } from 'react-redux'
import assignmentSelectors from '../../../../../store/selectors/assignment.selectors'
import { isNumber } from 'lodash'
import PatternIcon from '../../../../../components/common/PatternIcon'
import logo from '../../../../../../assets/images/sense_logo2.png'
import MarkdownRenderer from '../../../../../components/common/markdown-renderer'
import InlineDiffRenderer from './InlineDiffRenderer'
import useRubricContext from '../../../../../hooks/useRubricContext'

const { Title, Text, Paragraph } = Typography

const PDF_EXPORT_VIEWS = {
    REGULAR: 'regular',
    DIFF: 'diff',
}

const QuestionContentPrint = ({ question, questionRubric, exportView }) => {
    const showDiff = exportView === PDF_EXPORT_VIEWS.DIFF
    const showCurrentOnly = exportView === PDF_EXPORT_VIEWS.REGULAR
    const categories = questionRubric?.categories || []

    const renderContent = (current, comparison, isFinalized) => {
        if (showCurrentOnly) {
            return <MarkdownRenderer content={current} />
        }
        if (showDiff) {
            const oldValue = isFinalized ? comparison : current
            const newValue = isFinalized ? current : comparison
            return <InlineDiffRenderer oldValue={oldValue} newValue={newValue} />
        }
        return <MarkdownRenderer content={current} />
    }

    return (
        <div key={questionRubric?.id || question.id}>
            <Title level={3} style={{ margin: 0 }}>
                {questionRubric?.name || question.name}
            </Title>
            <Paragraph>
                <Text>{question.content}</Text>
            </Paragraph>
            {question.gradingReferenceContent && (
                <div style={{ marginBottom: '24px' }}>
                    {showCurrentOnly ? (
                        <Paragraph style={{ marginBottom: '24px' }}>
                            <Flex align='center' gap={8} style={{ marginBottom: '12px' }}>
                                <Text strong type='secondary'>Grading reference content:</Text>
                            </Flex>
                            <MarkdownRenderer content={question.gradingReferenceContent} type="secondary" />
                        </Paragraph>
                    ) : question.comparisonGradingReference != null && question.comparisonGradingReference !== '' ? (
                        <div style={{ marginBottom: '24px' }}>
                            <Text strong style={{ fontSize: '16px', marginBottom: '8px', display: 'block' }}>
                                Grading Reference
                            </Text>
                            {(() => {
                                const current = String(question.gradingReferenceContent || '')
                                const comparison = String(question.comparisonGradingReference || '')
                                const isFinalized = question.gradingReferenceChangeStatus === 'Done' || question.gradingReferenceChangeStatus === 'finalized'
                                return renderContent(current, comparison, isFinalized)
                            })()}
                        </div>
                    ) : (
                        <Paragraph style={{ marginBottom: '24px' }}>
                            <Flex align='center' gap={8} style={{ marginBottom: '12px' }}>
                                <Text strong type='secondary'>Grading reference content:</Text>
                            </Flex>
                            <MarkdownRenderer content={question.gradingReferenceContent} type="secondary" />
                        </Paragraph>
                    )}
                </div>
            )}
            <Flex justify='space-between'>
                {isNumber(questionRubric?.rubric?.weight) && (
                    <Paragraph>
                        <Text strong>Weight:</Text> {questionRubric.rubric.weight} points
                    </Paragraph>
                )}
                {question.wordsLimit && (
                    <Paragraph>
                        <Text strong>Limit:</Text> {question.wordsLimit} words
                    </Paragraph>
                )}
            </Flex>
            <Title level={4} style={{ margin: 0 }}>
                Categories
            </Title>
            <Table
                dataSource={categories}
                rowKey="id"
                pagination={false}
                size="small"
                scroll={{ x: 'max-content' }}
                columns={[
                    {
                        title: 'Name',
                        dataIndex: 'name',
                        key: 'name',
                        width: '20%',
                        render: (name, category) => (
                            <Space>
                                <PatternIcon id={category.id} colorId={category.colorId} showId={false} />
                                <Text strong>{name}</Text>
                            </Space>
                        ),
                    },
                    {
                        title: 'Points',
                        dataIndex: 'grade',
                        key: 'grade',
                        width: '8%',
                        render: (grade) => grade !== null ? grade : '-',
                    },
                    {
                        title: 'Description',
                        dataIndex: 'description',
                        key: 'description',
                        width: '50%',
                        render: (description, category) => {
                            if (!description) return '-'
                            if (showCurrentOnly) {
                                return <MarkdownRenderer content={description} />
                            }
                            if (category.comparisonDescription != null && category.comparisonDescription !== '') {
                                const current = String(category.description || '')
                                const comparison = String(category.comparisonDescription || '')
                                const isFinalized = category.descriptionChangeStatus === 'Done' || category.descriptionChangeStatus === 'finalized'
                                return renderContent(current, comparison, isFinalized)
                            }
                            return <MarkdownRenderer content={description} />
                        },
                    },
                    {
                        title: 'Pre-defined feedback',
                        dataIndex: 'feedback',
                        key: 'feedback',
                        width: '22%',
                        render: (feedback) => feedback || '-',
                    },
                ]}
            />
        </div>
    )
}

const RubricPreviewPrint = ({ exportView = PDF_EXPORT_VIEWS.REGULAR }) => {
    const assignmentName = useSelector(assignmentSelectors.name)

    const { questions, isFetching } = useRubricContext()

    if (isFetching) return <Skeleton active />
    if (!questions) return <Alert message="Failed to load questions" type='error' showIcon />

    return (
        <div style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
            <Flex justify='space-between' align='center' style={{ marginBottom: '12px' }}>
                <Title level={1} style={{ fontSize: '1.8rem', margin: 0 }}>
                    {assignmentName}
                </Title>
                <a href='https://www.sense.education/'>
                    <img
                        src={logo}
                        alt='Sense Education'
                        style={{
                            width: '120px',
                            objectFit: 'contain',
                        }}
                    />
                </a>
            </Flex>
            {questions.map(question => (
                <div key={question.id}>
                    <Title level={2} style={{ margin: 0, fontSize: '1.5rem' }}>
                        {question.name}
                    </Title>
                    <Paragraph>
                        <Text>{question.content}</Text>
                    </Paragraph>
                    {(question.questionRubrics || []).map(questionRubric => (
                        <div key={questionRubric.id} style={{ marginLeft: '1.5rem', marginTop: '1rem' }}>
                            <QuestionContentPrint 
                                question={question}
                                questionRubric={questionRubric}
                                exportView={exportView}
                            />
                        </div>
                    ))}
                    <Divider />
                </div>
            ))}
        </div>
    )
}

export default RubricPreviewPrint

